import type { AdminPickerListItemDto, AdminPickerTotalsDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { PickersFilterBar } from './_components/PickersFilterBar';
import { buildPickerColumns } from './_lib/pickerColumns';
import { filterKeyOf, hasActiveFilters, pickerFiltersSchema } from './_lib/pickerFilters';
import { PickerDetailDrawer } from './PickerDetailDrawer';
import { useAdminPickersList } from '../../api/admin-pickers';
import { RequirePermission } from '../../auth';
import { AdminDataGrid } from '../../components/data-grid';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { useUrlFilters } from '../../hooks/useUrlFilters';

/**
 * `/pickers` — «Подборщики» (AUT-400 / CP-10a), after
 * `design/mockups/admin/pickers-list.html`.
 *
 * Gated on `verify_inspectors` around the component that OWNS the query,
 * so an admin without it sends no request at all. Name / phone arrive
 * already redacted without `view_pii`; the SPA never un-redacts. Filters
 * live in the URL; the cursor API is bridged to the grid's pages by
 * `useCursorPagination` (next / previous, no page numbers — there is no
 * total per filter). Row click opens the detail drawer; the row id is the
 * picker's user id.
 *
 * Not here yet: the company column / filter / card (CP-10b) and the
 * «Журнал» tab.
 */
const Totals = ({ totals }: { totals: AdminPickerTotalsDto | undefined }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Typography variant="body2" color="text.secondary">
      {totals
        ? t('pickers.totals', {
            all: totals.all,
            disabled: totals.disabled,
            fresh: totals.newLast7d,
          })
        : t('pickers.subtitle')}
    </Typography>
  );
};

const PickersListPageInner = (): ReactElement => {
  const { t } = useTranslation();
  const { filters, setFilters, reset } = useUrlFilters(pickerFiltersSchema);
  const pager = useCursorPagination();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filterKey = filterKeyOf(filters);
  useEffect(() => {
    pager.reset();
    // `pager.reset` is stable; re-run only when the filters change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const query = useAdminPickersList({
    state: filters.state,
    ...(filters.source ? { source: filters.source } : {}),
    ...(filters.cityCountry ? { cityCountry: filters.cityCountry } : {}),
    ...(filters.city ? { city: filters.city } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    cursor: pager.cursor,
    limit: pager.pageSize,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const nextCursor = query.data?.nextCursor ?? null;

  useEffect(() => {
    if (query.isSuccess) pager.onResult(nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, nextCursor]);

  const columns = useMemo(() => buildPickerColumns(t), [t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
          {t('pickers.title')}
        </Typography>
        <Totals totals={query.data?.totals} />
      </Box>

      <PickersFilterBar
        filters={filters}
        setFilters={setFilters}
        canClear={hasActiveFilters(filters)}
        onClear={reset}
      />

      <AdminDataGrid<AdminPickerListItemDto>
        rows={items}
        columns={columns}
        getRowId={(row) => row.userId}
        rowCount={pager.rowCount(items.length)}
        loading={query.isLoading || query.isFetching}
        error={query.error}
        paginationModel={pager.paginationModel}
        onPaginationModelChange={pager.onPaginationModelChange}
        sortModel={[]}
        onSortModelChange={() => undefined}
        onRowClick={(row) => setSelectedId(row.userId)}
        emptyState={
          <Typography variant="body2" color="text.secondary">
            {t('pickers.empty')}
          </Typography>
        }
      />

      <PickerDetailDrawer pickerId={selectedId} onClose={() => setSelectedId(null)} />
    </Box>
  );
};

const Observed = observer(PickersListPageInner);

/** Whole-page gate: no `verify_inspectors` → the default 403 panel, no requests. */
export const PickersListPage = (): ReactElement => (
  <RequirePermission perm="verify_inspectors">
    <Observed />
  </RequirePermission>
);
